import React, { useState } from 'react';
import { Music, Plus, X, ExternalLink } from 'lucide-react';

const MusicPreferences = ({ preferences, onUpdate }) => {
  const [enabled, setEnabled] = useState(preferences?.enabled || false);
  const [selectedGenres, setSelectedGenres] = useState(preferences?.genres || []);
  const [artists, setArtists] = useState(preferences?.artists || []);
  const [newArtist, setNewArtist] = useState('');
  const [concertGoer, setConcertGoer] = useState(preferences?.concertGoer || false);
  const [playsInstrument, setPlaysInstrument] = useState(preferences?.playsInstrument || false);
  const [instruments, setInstruments] = useState(preferences?.instruments || []);
  const [newInstrument, setNewInstrument] = useState('');

  const musicGenres = [
    'Pop', 'Rock', 'Hip Hop', 'R&B', 'Country', 'Electronic/EDM',
    'Jazz', 'Classical', 'Blues', 'Folk', 'Reggae', 'Metal',
    'Punk', 'Indie', 'Soul', 'Funk', 'Latin', 'K-Pop',
    'Alternative', 'Dance', 'Gospel', 'Opera', 'Other'
  ];

  const toggleGenre = (genre) => {
    setSelectedGenres(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const addArtist = () => {
    if (newArtist.trim() && !artists.includes(newArtist.trim())) {
      setArtists([...artists, newArtist.trim()]);
      setNewArtist('');
    }
  };

  const removeArtist = (artistToRemove) => {
    setArtists(artists.filter(a => a !== artistToRemove));
  };

  const addInstrument = () => {
    if (newInstrument.trim() && !instruments.includes(newInstrument.trim())) {
      setInstruments([...instruments, newInstrument.trim()]);
      setNewInstrument('');
    }
  };

  const removeInstrument = (instrumentToRemove) => {
    setInstruments(instruments.filter(i => i !== instrumentToRemove));
  };

  const handleSave = async () => {
    const musicData = {
      enabled,
      genres: selectedGenres,
      artists,
      concertGoer,
      playsInstrument,
      instruments: playsInstrument ? instruments : []
    };

    await onUpdate(musicData);
  };

  const handleSpotifyConnect = () => {
    // This will be implemented when you add Spotify OAuth
    alert('Spotify integration coming soon!');
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Music className="w-6 h-6 text-pink-500" />
          <h3 className="text-xl font-semibold">Music Preferences</h3>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-pink-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-pink-500"></div>
        </label>
      </div>

      {enabled && (
        <div className="space-y-6">
          {/* Spotify Integration */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-green-900">Connect Spotify</h4>
                <p className="text-sm text-green-700">
                  Auto-import your favorite artists and genres
                </p>
              </div>
              <button
                onClick={handleSpotifyConnect}
                className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition"
              >
                <ExternalLink className="w-4 h-4" />
                Connect
              </button>
            </div>
          </div>

          {/* Music Genres */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Favorite Genres
            </label>
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(120px, 1fr))', gap:8}}>
              {musicGenres.map(genre => (
                <button
                  key={genre}
                  onClick={() => toggleGenre(genre)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                    selectedGenres.includes(genre)
                      ? 'bg-pink-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          {/* Favorite Artists */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Favorite Artists
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={newArtist}
                onChange={(e) => setNewArtist(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addArtist()}
                placeholder="Add an artist..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
              <button
                onClick={addArtist}
                className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {artists.map(artist => (
                <span
                  key={artist}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-pink-100 text-pink-700 rounded-full text-sm"
                >
                  {artist}
                  <button
                    onClick={() => removeArtist(artist)}
                    className="hover:text-pink-900"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Additional Music Info */}
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:16}}>
            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
              <input
                type="checkbox"
                checked={concertGoer}
                onChange={(e) => setConcertGoer(e.target.checked)}
                className="w-5 h-5 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
              />
              <span className="text-gray-700">I enjoy going to concerts</span>
            </label>

            <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition">
              <input
                type="checkbox"
                checked={playsInstrument}
                onChange={(e) => setPlaysInstrument(e.target.checked)}
                className="w-5 h-5 text-pink-500 border-gray-300 rounded focus:ring-pink-500"
              />
              <span className="text-gray-700">I play an instrument</span>
            </label>
          </div>

          {/* Instruments (if applicable) */}
          {playsInstrument && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Instruments I Play
              </label>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={newInstrument}
                  onChange={(e) => setNewInstrument(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addInstrument()}
                  placeholder="Add an instrument..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                />
                <button
                  onClick={addInstrument}
                  className="px-4 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {instruments.map(instrument => (
                  <span
                    key={instrument}
                    className="inline-flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                  >
                    {instrument}
                    <button
                      onClick={() => removeInstrument(instrument)}
                      className="hover:text-purple-900"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4">
            <button
              onClick={handleSave}
              className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition font-medium"
            >
              Save Music Preferences
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MusicPreferences;
